import { ReactNode } from 'react'
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  healthyFinancialInstitutionIds,
  INSTITUTIONS_MAP,
} from '@/src/constants/businessConstants'
import {
  FinancialAccount,
  FinancialType,
  TransactionSyncState,
} from '@/reducers/finances/financialAccountsReducer'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { FinancialAccountWithAdminInfo } from '@/reducers/admin/financialAccountsReducer'
import { StatementStatusType } from '@/actions/financialAccountActions'
import { Icon, Link, Text, Tooltip } from '@/ui'

interface PopupProps {
  title?: string
  body: ReactNode
  link?: string
  linkText?: string
}

export const getPlaidPopup = (institution: PlaidItem): PopupProps | null => {
  switch (institution.institutionId) {
    case INSTITUTIONS_MAP.CHASE:
      return {
        title: `Managing ${institution.institutionName} accounts`,
        body: 'Due to Chase policy, you can only stop sharing account connections via their website or app.',
        link: ' https://chase.com',
        linkText: 'Chase.com',
      }
    case INSTITUTIONS_MAP.WELLS_FARGO:
      return {
        title: `Managing ${institution.institutionName} accounts`,
        body: (
          <>
            <Text>
              Due to Wells Fargo policy, you can only stop sharing account
              connections via their website or app.
            </Text>

            <Text>
              Doing this will also disconnect the account from other personal
              finance apps that you use (i.e. Venmo, Robinhood, Mint).
            </Text>
          </>
        ),
        link: 'https://wellsfargo.com',
        linkText: 'WellsFargo.com',
      }
    default:
      return null
  }
}

export const getPlaidButtonLabel = (
  institution: PlaidItem,
  disconnectedOn: string,
  isInRolledOutInstitutions: boolean
) => {
  const popup = getPlaidPopup(institution)

  return disconnectedOn ? (
    'Reconnect'
  ) : isInRolledOutInstitutions ? (
    <>
      Add Accounts
      {popup && (
        <Tooltip>
          <Tooltip.Trigger asChild>
            <Icon
              className="ml-2.5"
              name="question circle outline"
              icon={faQuestionCircle}
              color={'darkGray'}
            />
          </Tooltip.Trigger>

          <Tooltip.Content>
            <Text>{popup.title && <div>{popup.title}</div>}</Text>

            {popup.body}

            {popup.link && (
              <Link href={popup.link} newPage>
                {popup.linkText || popup.link}
              </Link>
            )}
          </Tooltip.Content>
        </Tooltip>
      )}
    </>
  ) : (
    'Add/Remove Account'
  )
}

export const getAccountsAwaitingConfirmation = (
  accounts: FinancialAccount[],
  rolledOutInstitutionIds: string[]
) => {
  return accounts.filter(
    (account) =>
      !account.inactive &&
      !account.isHidden &&
      account.transactionSyncState === TransactionSyncState.PENDING &&
      account.plaidInstitutionId &&
      rolledOutInstitutionIds.includes(account.plaidInstitutionId)
  )
}

export const checkIsInRolledOutInstitutions = (
  rolledOutInstitutionIds: string[],
  institutionId: string
) => Boolean(rolledOutInstitutionIds?.includes(institutionId))

export const hasLimitedBankAccess = (
  account: FinancialAccount | FinancialAccountWithAdminInfo
) =>
  account.accountType === FinancialType.PLAID &&
  account.bankAccessEnabledAt !== null

export const hasPlaidStatements = (
  account: FinancialAccount | FinancialAccountWithAdminInfo
) =>
  account.accountType === FinancialType.PLAID &&
  account.statementPermissions === StatementStatusType.PLAID_STATEMENT

export const hasPlaidStatementsDisabled = (account: FinancialAccount) =>
  account.accountType === FinancialType.PLAID &&
  account.statementPermissions === 'plaid_statement_disabled'

export const isAutoStatementUploadAccount = (account: FinancialAccount) =>
  hasPlaidStatements(account) || hasLimitedBankAccess(account)

export const isManualPlaidStatementUploadAccount = (
  account: FinancialAccount
) =>
  account.accountType === FinancialType.PLAID &&
  !isAutoStatementUploadAccount(account)

export const isHealthyInstitution = (institutionId: string | null) =>
  healthyFinancialInstitutionIds.includes(institutionId ?? '')

export const hasPlaidStatementsAccount = (accounts: FinancialAccount[]) =>
  accounts.some(hasPlaidStatements)

export const hasPlaidStatementsDisabledAccount = (
  accounts: FinancialAccount[]
) => accounts.some(hasPlaidStatementsDisabled)

export const hasHealthyManualStatementUploadAccount = (
  accounts: FinancialAccount[]
) =>
  accounts.some(
    (account) =>
      isManualPlaidStatementUploadAccount(account) &&
      isHealthyInstitution(account.plaidInstitutionId)
  )
